import { useEffect, useRef, useState } from 'react';

import { LOCALE_DEFAULT, LOCALE_ES, useI18nReact, useTranslate } from '@growtherapy/i18n';
import { Modal, ModalBody, ModalFooter, ModalHeader, RadioButton, RadioGroup } from '@growtherapy/sprout-ui';

import { logger } from '../datadog/logger';
import { setStoredLanguagePreference } from '../i18n/languagePreference';
import { useTrackEvent } from '../segment/segment';
import { EventTypeEnum } from '../segment/types';
import { normalizeError } from '../utils';

type LanguageSwitcherModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type LanguageRadioGroupProps = {
  selectedLocale: string;
  onSelect: (locale: string) => void;
};

function LanguageRadioGroup({ selectedLocale, onSelect }: LanguageRadioGroupProps) {
  const t = useTranslate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelect(e.target.value);
  };

  const options = [
    <RadioButton key={LOCALE_DEFAULT} label={t('telehealth-core:settings.english')} value={LOCALE_DEFAULT} />,
    <RadioButton key={LOCALE_ES} label={t('telehealth-core:settings.spanish')} value={LOCALE_ES} />,
  ];

  return (
    <RadioGroup
      onChange={handleChange}
      value={selectedLocale}
      layout='column'
      name='language-preference'
      aria-label={t('telehealth-core:settings.selectYourPreferredLanguage')}
    >
      {options}
    </RadioGroup>
  );
}

export function LanguageSwitcherModal({ isOpen, onClose }: LanguageSwitcherModalProps) {
  const t = useTranslate();
  const i18n = useI18nReact();
  const [selectedLocale, setSelectedLocale] = useState(i18n.language);
  const { userClicked, userViewed } = useTrackEvent();
  const hasTrackedViewRef = useRef(false);

  useEffect(
    function syncLocalStateToI18n() {
      if (isOpen) {
        setSelectedLocale(i18n.language);
      }
    },
    [isOpen, i18n.language],
  );

  useEffect(
    function firePreferredLangModalViewedEvent() {
      if (isOpen && !hasTrackedViewRef.current) {
        hasTrackedViewRef.current = true;
        userViewed(EventTypeEnum.PREFERRED_LANGUAGE_MODAL_VIEWED, { current: i18n.language });
      }
      if (!isOpen) {
        hasTrackedViewRef.current = false;
      }
    },
    [isOpen, userViewed, i18n.language],
  );

  const handleDismiss = () => {
    setSelectedLocale(i18n.language);
    onClose();
  };

  const handleSave = async () => {
    if (selectedLocale !== i18n.language) {
      try {
        await i18n.changeLanguage(selectedLocale);
        setStoredLanguagePreference(selectedLocale);
        userClicked(EventTypeEnum.LANGUAGE_SWITCHER_MODAL_SAVE, {
          previous: i18n.language,
          current: selectedLocale,
        });
      } catch (error) {
        logger.error(
          'Failed to change language from language switcher modal',
          { selectedLocale, previousLanguage: i18n.language },
          normalizeError(error),
        );
        return;
      }
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleDismiss} className='rebrand:rounded-none sm:!top-1/2 sm:!-translate-y-1/2'>
      <ModalHeader>{t('telehealth-core:settings.selectYourPreferredLanguage')}</ModalHeader>
      <ModalBody>
        <LanguageRadioGroup selectedLocale={selectedLocale} onSelect={setSelectedLocale} />
      </ModalBody>
      <ModalFooter
        actions={[
          {
            key: 'close',
            actionContent: t('telehealth-core:settings.close'),
            onClick: handleDismiss,
            use: 'secondary',
          },
          {
            key: 'save',
            use: 'primary',
            onClick: handleSave,
            actionContent: t('telehealth-core:settings.save'),
          },
        ]}
      />
    </Modal>
  );
}
